import { fetchEventSource } from "@microsoft/fetch-event-source";
import { AUTHORIZATION_HEADER_KEY, WORKSPACE_HEADER_KEY } from "apis/localhost";
import { UPLOAD_STATUSES } from "components/Modal/UploadFilesModal/constants";
import { FILE_SOURCES } from "layout/documentUploader/constants";
import { selectAddInAccessToken } from "slices/addIn/authentication/selectors";
import { selectEffectiveWorkspaceId } from "slices/addIn/metadata/selectors";
import {
  setEventSource,
  updateFileUploadState,
} from "slices/workspace/foldersSlice";
import { setDocumentData } from "slices/writing/currentDocument/currentDocumentSlice";
import { setScoringUnavailable } from "slices/writing/scoring/analysis/analysisSlice";
import { isAddInWebView } from "utils/add-in";
import { getSseErrorMessage } from "utils/index";

import { modalTypes } from "../../../constants/types";
import { showModal } from "../../app/actions/modals";
import { setActiveSource } from "../chooseFilesModalSlice";

const uploadSeenMessageIds = new Set();

export const resetUploadSSEState = () => {
  uploadSeenMessageIds.clear();
};

const MAX_SSE_RETRIES = 3;

export const setupSSEConnection = (
  controller,
  flowId,
  { dispatch, getState, skipDocumentData = false } = {}
) => {
  uploadSeenMessageIds.clear();

  let resolveConnection;
  let rejectConnection;
  const connectionReady = new Promise((resolve, reject) => {
    resolveConnection = resolve;
    rejectConnection = reject;
  });

  let retryCount = 0;

  const url = flowId
    ? `/document/queue/stream/connect?flowId=${flowId}`
    : "/document/queue/progress";

  const headers = {};

  if (isAddInWebView()) {
    const state = getState();
    const workspaceId = selectEffectiveWorkspaceId(state);
    if (workspaceId) {
      headers[WORKSPACE_HEADER_KEY] = workspaceId;
    }

    headers[AUTHORIZATION_HEADER_KEY] =
      `Bearer ${selectAddInAccessToken(state)}`;
  }

  fetchEventSource(url, {
    signal: controller.signal,
    openWhenHidden: true,
    headers,
    onopen(response) {
      if (response.ok) {
        resolveConnection();
      } else {
        rejectConnection(
          new Error(`SSE connection failed: ${response.status}`)
        );
      }
    },
    onerror(error) {
      retryCount += 1;
      if (retryCount >= MAX_SSE_RETRIES) {
        console.warn(
          `SSE connection failed after ${MAX_SSE_RETRIES} attempts, giving up.`,
          error
        );
        rejectConnection(error);
        throw error;
      }
      console.warn(
        `SSE connection error (attempt ${retryCount}/${MAX_SSE_RETRIES}), retrying...`,
        error
      );
    },
    onmessage: function (e) {
      const eventType = e.event;

      // Update headers object in-place so auto-reconnects send the latest ID
      if (e.id) headers["Last-Event-ID"] = e.id;

      // Deduplicate replayed messages
      if (e.id && uploadSeenMessageIds.has(e.id)) return;
      if (e.id) uploadSeenMessageIds.add(e.id);

      if (typeof eventType === "string") {
        if (eventType === "connected" || eventType === "heartbeat") return;

        const parsedData = JSON.parse(e.data);
        if (isAddInWebView()) {
          console.warn("eventType", eventType);
          console.warn("parsedData", parsedData);
        }

        switch (eventType) {
          case "document-queued-event":
            dispatch(
              updateFileUploadState({
                hash: parsedData.hash,
                status: UPLOAD_STATUSES.IN_PROGRESS,
                id: parsedData.id,
              })
            );
            if (!skipDocumentData) {
              dispatch(
                setDocumentData({
                  syncTasks: [
                    {
                      id: parsedData.id || "initial",
                      hash: parsedData.hash,
                      name: parsedData.name,
                      status: UPLOAD_STATUSES.IN_PROGRESS,
                      error: parsedData.error,
                    },
                  ],
                })
              );
            }
            break;

          case "document-completed-event":
            dispatch(
              updateFileUploadState({
                hash: parsedData.hash,
                status: UPLOAD_STATUSES.COMPLETED,
                id: parsedData.id,
              })
            );
            if (!skipDocumentData) {
              dispatch(
                setDocumentData({
                  isSyncing: false,
                  syncTasks: [
                    {
                      id: parsedData.id,
                      hash: parsedData.hash,
                      name: parsedData.name,
                      status: UPLOAD_STATUSES.COMPLETED,
                      error: parsedData.error,
                    },
                  ],
                })
              );
            }
            break;

          case "document-scoring-updated-event":
            if (!skipDocumentData) {
              dispatch(
                setDocumentData({ hasScoringData: parsedData.hasScoringData })
              );
            }
            dispatch(setScoringUnavailable(!parsedData.hasScoringData));
            break;

          case "document-file-failed-event": {
            const documentFileFailedMessage = getSseErrorMessage(
              parsedData.error
            );
            dispatch(
              updateFileUploadState({
                hash: parsedData.hash,
                status: UPLOAD_STATUSES.FAILED,
                error: documentFileFailedMessage,
              })
            );
            if (!skipDocumentData) {
              dispatch(
                setDocumentData({
                  isSyncing: false,
                  syncTasks: [
                    {
                      id: parsedData.id,
                      hash: parsedData.hash,
                      name: parsedData.name,
                      status: UPLOAD_STATUSES.FAILED,
                      error: documentFileFailedMessage,
                    },
                  ],
                })
              );
            }
            break;
          }

          case "rag-document-indexing-started-event":
            dispatch(
              setDocumentData({
                syncTasks: [
                  {
                    id: parsedData.id || "initial",
                    hash: parsedData.hash,
                    name: parsedData.name,
                    status: UPLOAD_STATUSES.IN_PROGRESS,
                    error: parsedData.error,
                  },
                ],
              })
            );
            break;

          case "rag-document-indexing-completed-event":
            dispatch(
              updateFileUploadState({
                hash: parsedData.hash,
                status: UPLOAD_STATUSES.COMPLETED,
                id: parsedData.id,
              })
            );
            dispatch(
              setDocumentData({
                isSyncing: false,
                syncTasks: [
                  {
                    id: parsedData.id,
                    hash: parsedData.hash,
                    name: parsedData.name,
                    status: UPLOAD_STATUSES.COMPLETED,
                    error: parsedData.error,
                  },
                ],
              })
            );
            break;

          case "rag-document-indexing-failed-event": {
            const ragIndexingFailedMessage = getSseErrorMessage(
              parsedData.error
            );
            dispatch(
              updateFileUploadState({
                hash: parsedData.hash,
                status: UPLOAD_STATUSES.FAILED,
                error: ragIndexingFailedMessage,
              })
            );
            dispatch(
              setDocumentData({
                isSyncing: false,
                syncTasks: [
                  {
                    id: parsedData.id,
                    hash: parsedData.hash,
                    name: parsedData.name,
                    status: UPLOAD_STATUSES.FAILED,
                    error: ragIndexingFailedMessage,
                  },
                ],
              })
            );
            break;
          }
          default:
            console.warn(`Unhandled SSE event type received: ${eventType}`);
        }
      } else {
        console.error("Invalid SSE event type received:", eventType);
      }
    },
  });

  return connectionReady;
};

export const startSSEConnectionNoRedux = (flowId) => (dispatch, getState) => {
  const controller = new AbortController();
  controller.signal.addEventListener("abort", () => {
    console.warn("Client closed the SSE connection.");
  });

  setupSSEConnection(controller, flowId, {
    dispatch,
    getState,
    skipDocumentData: true,
  });

  // return teardown method
  return () => {
    controller.abort();
  };
};

export const startSSEConnection = (flowId) => async (dispatch, getState) => {
  // Only one of these is meant to be live at a time (state.folders.eventSource is a single
  // slot, not a list) — abort whatever's already there before opening another, otherwise each
  // save/rescan leaks its own long-lived connection that never gets closed, silently piling up
  // against the browser's per-origin connection limit until new requests can't get a socket.
  const previousController = getState().folders.eventSource;
  if (previousController) {
    previousController.abort();
  }

  const controller = new AbortController();
  controller.signal.addEventListener("abort", () => {
    console.warn("Client closed the SSE connection.");
  });
  await setupSSEConnection(controller, flowId, { dispatch, getState });
  dispatch(setEventSource(controller));
};

export const showFilePickerModal = (modalProps) => (dispatch) => {
  dispatch(setActiveSource(FILE_SOURCES.LOCAL));
  dispatch(showModal(modalTypes.CHOOSE_FILES, modalProps));
};
